import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  getMRSitesReports,
  mrSitesReportsGetByReportID,
  documentAssistantAsk,
  documentAssistantChatHistoryGet,
  documentAssistantChatHistoryDelete,
  documentAssistantChatSessionRename,
  documentAssistantUpload,
  saveReportDocument,
} from 'apiServices/reducer/apiService';
import CustomSuccessModal from '../../../components/CustomSuccessModal';
import CustomAlertModal from '../../../components/CustomAlertModal';
import './AskAcme.css';

const getCategorySelectionState = (itemIds, selectedIds) => {
  if (!itemIds.length) return { all: false, some: false };
  const selectedCount = itemIds.filter((id) => selectedIds.includes(id)).length;
  return {
    all: selectedCount === itemIds.length,
    some: selectedCount > 0 && selectedCount < itemIds.length,
  };
};

const AI_DISCLAIMER_TEXT =
  'This summary was generated by AI and may contain inaccuracies. Please review carefully.';

const AskAcmeDisclaimer = () => (
  <p className="ask-acme-disclaimer" role="note">
    <svg className="ask-acme-disclaimer-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path d="M12 3 2.8 20h18.4L12 3Z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
      <path d="M12 10v5M12 17.5v.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
    <span>
      <strong>Disclaimer:</strong> {AI_DISCLAIMER_TEXT}
    </span>
  </p>
);

const SUGGESTED_QUESTIONS = [
  {
    id: 'summarize',
    label: 'Summary',
    question: 'Summarize this report?',
    icon: 'doc',
  },
  {
    id: 'findings',
    label: 'Key findings',
    question: 'What are the key findings in this report?',
    icon: 'findings',
  },
  {
    id: 'abnormal',
    label: 'Abnormal values',
    question: 'Which values are abnormal?',
    icon: 'chart',
  },
  {
    id: 'risks',
    label: 'Health risks',
    question: 'Are there any potential health risks mentioned in this report?',
    icon: 'risk',
  },
  {
    id: 'monitor',
    label: 'Monitor over time',
    question: 'Is there anything I should monitor over time?',
    icon: 'monitor',
  },
];

const SuggestedQuestionIcon = ({ type }) => {
  const common = { width: 20, height: 20, viewBox: '0 0 24 24', fill: 'none', 'aria-hidden': true };
  if (type === 'doc') {
    return (
      <svg {...common}>
        <path d="M8 4h6l4 4v12H8V4Z" stroke="currentColor" strokeWidth="1.7" />
        <path d="M14 4v4h4M10 13h6M10 17h4" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
      </svg>
    );
  }
  if (type === 'findings') {
    return (
      <svg {...common}>
        <path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2" stroke="currentColor" strokeWidth="1.7" />
        <path d="M9 5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v1H9V5Z" stroke="currentColor" strokeWidth="1.7" />
        <path d="M9 12l2 2 4-4" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  }
  if (type === 'chart') {
    return (
      <svg {...common}>
        <path d="M4 19V9M10 19V5M16 19v-7M22 19H2" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
      </svg>
    );
  }
  if (type === 'risk') {
    return (
      <svg {...common}>
        <path d="M12 3 3 19h18L12 3Z" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" />
        <path d="M12 10v4M12 16.5v.5" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
      </svg>
    );
  }
  return (
    <svg {...common}>
      <path d="M4 19V9l4 2 4-6 4 4 4-2v12" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M4 19h16" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
    </svg>
  );
};

const MSGS_STORE_PREFIX = 'askAcme_session_msgs_';
const DOC_MAP_PREFIX = 'askAcme_docmap_';
const HISTORY_META_PREFIX = 'askAcme_history_meta_';
const UPLOADS_META_PREFIX = 'askAcme_uploads_meta_';
const REPORT_FILE_WAIT_MS = 5 * 60 * 1000;
const REPORT_FILE_WATCH_MS = 15 * 60 * 1000;
const REPORT_FILE_POLL_MS = 30 * 1000;
const FILING_NOTICE_MS = 12 * 1000;

const nowIso = () => new Date().toISOString();

const formatDateToYYYYMMDD = (date = new Date()) => {
  const d = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(d.getTime())) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const uploadsMetaKey = (userId) => `${UPLOADS_META_PREFIX}${userId}`;

const normalizeReportKey = (name) =>
  String(name || '')
    .replace(/\.[a-z0-9]+$/i, '')
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();

const remainingFileMinutes = (uploadedAt, now = Date.now()) => {
  const start = Date.parse(uploadedAt || '');
  if (!Number.isFinite(start)) return null;
  const remaining = REPORT_FILE_WAIT_MS - (now - start);
  if (remaining <= 0) return 0;
  return Math.max(1, Math.ceil(remaining / 60000));
};

const getUploadFilingLabel = (doc, now = Date.now()) => {
  if (doc?.filedGroupName) return `Now in ${doc.filedGroupName}`;
  const mins = remainingFileMinutes(doc?.uploadedAt, now);
  if (mins == null) return 'Your Reports in ~5 min';
  if (mins > 0) return `Your Reports in ~${mins} min`;
  return 'Look in Your Reports categories';
};

const parseSavedReportId = (payload) => {
  const rows = unwrapRows(payload);
  const first = rows[0] || payload || {};
  const id =
    first.report_id ??
    first.ReportID ??
    first.reportId ??
    payload?.report_id ??
    payload?.ReportID ??
    payload?.reportId;
  const n = Number(id);
  return Number.isFinite(n) && n > 0 ? n : null;
};

const getSaveReportError = (payload, fallback = 'Failed to upload report.') => {
  if (payload == null) return fallback;
  if (typeof payload === 'string' && payload.trim()) return payload;
  return (
    payload.Return_Message ||
    payload.return_message ||
    payload.Message ||
    payload.message ||
    fallback
  );
};

const findNewSavedReport = (list, pending) => {
  if (!pending || !Array.isArray(list) || !list.length) return null;
  if (pending.reportId != null) {
    const byId = list.find((r) => String(r.reportId) === String(pending.reportId));
    if (byId) return byId;
  }
  const baseline = new Set((pending.baselineIds || []).map(String));
  const key = normalizeReportKey(pending.name);
  if (!key) return null;
  return (
    list.find((r) => {
      if (baseline.has(String(r.reportId))) return false;
      return normalizeReportKey(r.reportName) === key;
    }) || null
  );
};

const readUploadsMeta = (userId) => {
  if (userId == null) return [];
  try {
    const raw = localStorage.getItem(uploadsMetaKey(userId));
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const writeUploadsMeta = (userId, docs) => {
  if (userId == null) return;
  const meta = (Array.isArray(docs) ? docs : []).map((d) => ({
    documentId: Number(d.documentId),
    documentName: d.documentName || 'Untitled',
    embeddingStatus: d.embeddingStatus || 'Ready',
    fileName: d.fileName || d.documentName || 'file',
    fileType: d.fileType || '',
    uploadedAt: d.uploadedAt || null,
    baselineReportIds: Array.isArray(d.baselineReportIds) ? d.baselineReportIds : [],
    filedGroupName: d.filedGroupName || null,
    filedReportId: d.filedReportId ?? null,
  }));
  localStorage.setItem(uploadsMetaKey(userId), JSON.stringify(meta));
};

const unwrapRows = (payload) => {
  if (!payload) return [];
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload.Table)) return payload.Table;
  if (Array.isArray(payload.data)) return payload.data;
  if (Array.isArray(payload.sessions)) return payload.sessions;
  return [];
};

const unwrapHistoryRows = (payload) => {
  if (!payload) return [];
  if (Array.isArray(payload)) return payload;

  const buckets = [
    payload.Table,
    payload.Table1,
    payload.Table2,
    payload.data,
    payload.sessions,
    payload.Sessions,
    payload.messages,
    payload.Messages,
    payload.ChatHistory,
    payload.chatHistory,
  ];

  const rows = [];
  for (const bucket of buckets) {
    if (Array.isArray(bucket)) rows.push(...bucket);
  }
  return rows.length ? rows : unwrapRows(payload);
};

const pickHistoryQuestion = (row = {}) =>
  row.Question ??
  row.question ??
  row.UserQuestion ??
  row.user_question ??
  row.UserMessage ??
  row.user_message ??
  null;

const pickHistoryAnswer = (row = {}) =>
  row.Answer ??
  row.answer ??
  row.AssistantAnswer ??
  row.assistant_answer ??
  row.AssistantMessage ??
  row.assistant_message ??
  row.AIResponse ??
  row.ai_response ??
  row.Response ??
  row.response ??
  row.BotMessage ??
  row.bot_message ??
  null;

const pickHistoryDocumentId = (row = {}) => {
  const raw =
    row.DocumentID ??
    row.DocumentId ??
    row.documentId ??
    row.document_id ??
    row.DocID ??
    row.docId ??
    null;
  if (raw == null || raw === '') return null;
  const num = Number(raw);
  return Number.isFinite(num) && num > 0 ? num : null;
};

const pickHistoryDocumentName = (row = {}) =>
  row.DocumentName ??
  row.documentName ??
  row.document_name ??
  row.ReportName ??
  row.report_name ??
  row.FileName ??
  row.filename ??
  '';

const parsePrefixedAskQuestion = (text = '') => {
  const raw = String(text || '').trim();
  const match = raw.match(/^Using report "([^"]+)", answer this question:\s*([\s\S]+)/i);
  if (match) {
    return { reportName: match[1].trim(), question: match[2].trim() };
  }
  return { reportName: '', question: raw };
};

const combineMultiReportAnswers = (parts = []) => {
  const valid = (Array.isArray(parts) ? parts : []).filter(
    (p) => p && (String(p.answer || '').trim() || p.reportName)
  );
  if (!valid.length) return { answer: '', reportNames: [] };
  const reportNames = valid.map((p) => p.reportName).filter(Boolean);
  if (valid.length === 1) {
    return { answer: String(valid[0].answer || '').trim(), reportNames };
  }
  const answer = valid
    .map((p) => {
      const body = String(p.answer || '').trim() || 'No answer returned for this document.';
      const name = p.reportName || 'Report';
      return `📄 ${name}\n${body}`;
    })
    .join('\n\n————————————\n\n');
  return { answer, reportNames };
};

const uniqueDocs = (docs = []) => {
  const seen = new Set();
  const next = [];
  (Array.isArray(docs) ? docs : []).forEach((item) => {
    const documentId = Number(item?.documentId ?? item);
    if (!documentId || Number.isNaN(documentId) || seen.has(documentId)) return;
    seen.add(documentId);
    next.push({
      documentId,
      documentName: item?.documentName || item?.name || 'Document',
    });
  });
  return next;
};

const collectDocumentsFromHistoryRows = (rows = []) => {
  const docs = [];
  (Array.isArray(rows) ? rows : []).forEach((row) => {
    const documentId = pickHistoryDocumentId(row);
    if (!documentId) return;
    const parsed = parsePrefixedAskQuestion(pickHistoryQuestion(row) || '');
    docs.push({
      documentId,
      documentName: parsed.reportName || pickHistoryDocumentName(row) || 'Document',
    });
  });
  return uniqueDocs(docs);
};

const pickHistoryRole = (row = {}) => {
  const raw =
    row.Role ??
    row.role ??
    row.Sender ??
    row.sender ??
    row.MessageRole ??
    row.message_role ??
    row.MessageType ??
    row.message_type ??
    row.Type ??
    row.type ??
    '';
  const role = String(raw).toLowerCase().trim();
  if (!role) {
    if (row.IsUser === true || row.is_user === true || row.IsQuestion === true) return 'user';
    if (row.IsAssistant === true || row.is_assistant === true || row.IsAnswer === true) return 'assistant';
    return null;
  }
  if (
    role === 'user' ||
    role === 'human' ||
    role === 'q' ||
    role === 'question' ||
    role === '1' ||
    role === 'u'
  ) {
    return 'user';
  }
  if (
    role === 'assistant' ||
    role === 'bot' ||
    role === 'ai' ||
    role === 'system' ||
    role === 'a' ||
    role === 'answer' ||
    role === '2' ||
    role === 'response'
  ) {
    return 'assistant';
  }
  return null;
};

const pickHistoryContent = (row = {}) =>
  row.Content ??
  row.content ??
  row.Message ??
  row.message ??
  row.MessageText ??
  row.message_text ??
  row.Text ??
  row.text ??
  row.ChunkText ??
  null;

const scoreHistoryMessages = (msgs = []) => {
  if (!Array.isArray(msgs) || !msgs.length) return 0;
  const assistantText = msgs
    .filter((m) => m.role === 'assistant')
    .reduce((sum, m) => sum + String(m.message || '').length, 0);
  const userMsgs = msgs.filter((m) => m.role === 'user');
  const uniqueQuestions = new Set(
    userMsgs.map((m) => normalizeUserHistoryText(m.message)).filter(Boolean)
  );
  // Prefer combined multi-report answers over split per-report bubbles.
  return uniqueQuestions.size * 100000 + assistantText * 10 - msgs.length;
};

const preferRicherHistory = (primary, secondary) => {
  const a = Array.isArray(primary) ? primary : [];
  const b = Array.isArray(secondary) ? secondary : [];
  const aUserCount = a.filter((m) => m.role === 'user').length;
  const bUserCount = b.filter((m) => m.role === 'user').length;
  if (aUserCount !== bUserCount) return aUserCount > bUserCount ? a : b;
  // Same number of turns — prefer local cache (primary); it reflects the live chat thread.
  if (a.length !== b.length) return a.length >= b.length ? a : b;
  return scoreHistoryMessages(a) >= scoreHistoryMessages(b) ? a : b;
};

const normalizeUserHistoryText = (text = '') => {
  const raw = String(text || '').trim();
  if (!raw) return '';
  return parsePrefixedAskQuestion(raw).question;
};

const mergeSplitMultiReportMessages = (msgs = []) => {
  const list = Array.isArray(msgs) ? msgs : [];
  const out = [];
  let i = 0;
  while (i < list.length) {
    const cur = list[i];
    if (cur?.role !== 'user') {
      out.push(cur);
      i += 1;
      continue;
    }

    const question = normalizeUserHistoryText(cur.message);
    const parts = [];
    let j = i;

    while (j < list.length) {
      const userMsg = list[j];
      if (userMsg?.role !== 'user') break;
      const userQuestion = normalizeUserHistoryText(userMsg.message);
      if (userQuestion !== question) break;

      const parsed = parsePrefixedAskQuestion(userMsg.message);
      const reportName =
        parsed.reportName ||
        (Array.isArray(userMsg.attachedReports) ? userMsg.attachedReports[0] : '') ||
        '';
      j += 1;
      if (j < list.length && list[j]?.role === 'assistant') {
        parts.push({
          reportName,
          answer: list[j].message,
        });
        j += 1;
      } else if (parsed.reportName || reportName) {
        parts.push({ reportName, answer: '' });
      }
    }

    const combined = combineMultiReportAnswers(parts);
    const reportNames = Array.from(
      new Set(
        [
          ...(Array.isArray(cur.attachedReports) ? cur.attachedReports : []),
          ...combined.reportNames,
        ].filter(Boolean)
      )
    );

    out.push({
      ...cur,
      message: question || cur.message,
      attachedReports: reportNames,
    });
    if (combined.answer) {
      const assistantSrc = list[j - 1]?.role === 'assistant' ? list[j - 1] : null;
      out.push({
        ...(assistantSrc || {
          messageId: `api-a-merged-${cur.messageId}`,
          role: 'assistant',
          createdOn: cur.createdOn,
        }),
        role: 'assistant',
        message: combined.answer,
        basedOnReports: reportNames,
      });
    }
    i = j;
  }
  return out;
};

const sameReportNameSet = (a = [], b = []) => {
  const left = [...new Set((Array.isArray(a) ? a : []).map((n) => String(n || '').trim()).filter(Boolean))].sort();
  const right = [...new Set((Array.isArray(b) ? b : []).map((n) => String(n || '').trim()).filter(Boolean))].sort();
  if (left.length !== right.length) return false;
  return left.every((name, i) => name === right[i]);
};

/** Show "Using: …" only when the report set first appears (or changes). Later turns still query those reports. */
const getVisibleAttachedReports = (messages, index) => {
  const msg = messages?.[index];
  if (!msg || msg.role === 'assistant') return [];
  const reports = Array.isArray(msg.attachedReports)
    ? [...new Set(msg.attachedReports.map((n) => String(n || '').trim()).filter(Boolean))]
    : [];
  if (!reports.length) return [];

  for (let i = index - 1; i >= 0; i -= 1) {
    const prev = messages[i];
    if (prev?.role !== 'user') continue;
    const prevReports = Array.isArray(prev.attachedReports)
      ? prev.attachedReports.map((n) => String(n || '').trim()).filter(Boolean)
      : [];
    if (!prevReports.length) continue;
    if (sameReportNameSet(reports, prevReports)) return [];
    return reports;
  }
  return reports;
};

const cleanHistoryTitle = (title = '') => {
  const text = normalizeUserHistoryText(title);
  if (!text) return 'Previous chat';
  return text;
};

/** Build chat bubbles from DocumentAssistantChatHistoryGet payload / session rows. */
const buildMessagesFromHistoryApi = (payload, session) => {
  const sessionId = session?.sessionId;
  const rows = unwrapHistoryRows(payload).filter((row) => {
    if (sessionId == null) return true;
    const rowSession = row.session_id ?? row.SessionID ?? row.sessionId;
    return rowSession == null || String(rowSession) === String(sessionId);
  });

  const messages = [];
  const pushPair = (question, answer, createdOn, index) => {
    const when = createdOn || session?.updatedAt || nowIso();
    const parsed = parsePrefixedAskQuestion(question);
    const q = parsed.question;
    const a = answer != null ? String(answer).trim() : '';
    const reportNames = parsed.reportName ? [parsed.reportName] : [];
    if (q) {
      messages.push({
        messageId: `api-u-${sessionId}-${index}`,
        role: 'user',
        message: q,
        createdOn: when,
        attachedReports: reportNames,
      });
    }
    if (a) {
      messages.push({
        messageId: `api-a-${sessionId}-${index}`,
        role: 'assistant',
        message: a,
        createdOn: when,
        retrievedChunks: [],
        basedOnReports: reportNames,
      });
    }
  };

  const finalize = (list) => {
    const safe = Array.isArray(list) ? list : [];
    let next = safe;
    if (safe.length >= 2 && safe.every((m) => m.role === 'user')) {
      next = safe.map((m, i) => ({
        ...m,
        role: i % 2 === 0 ? 'user' : 'assistant',
        messageId: `api-${i % 2 === 0 ? 'u' : 'a'}-${sessionId}-${i}`,
      }));
    }
    return mergeSplitMultiReportMessages(next);
  };

  // 1) Prefer explicit Question + Answer on the same row
  let usedPairPath = false;
  rows.forEach((row, index) => {
    const question = pickHistoryQuestion(row);
    const answer = pickHistoryAnswer(row);
    if (question || answer) {
      usedPairPath = true;
      pushPair(
        question,
        answer,
        row.created_at ?? row.CreatedAt ?? row.updated_at ?? row.UpdatedAt,
        index
      );
    }
  });
  if (usedPairPath && messages.length) return finalize(messages);

  // 2) Role/content rows (or alternate user/assistant when role missing)
  const contentRows = rows
    .map((row, index) => ({
      row,
      index,
      role: pickHistoryRole(row),
      content: pickHistoryContent(row),
    }))
    .filter((item) => item.content);

  contentRows.forEach((item, seq) => {
    const inferredRole = item.role || (seq % 2 === 0 ? 'user' : 'assistant');
    const parsed = parsePrefixedAskQuestion(item.content);
    const text =
      inferredRole === 'user'
        ? parsed.question
        : String(item.content).trim();
    if (!text) return;
    const reportNames = parsed.reportName ? [parsed.reportName] : [];
    messages.push({
      messageId: `api-${inferredRole}-${sessionId}-${item.index}`,
      role: inferredRole,
      message: text,
      createdOn:
        item.row.created_at ??
        item.row.CreatedAt ??
        item.row.updated_at ??
        item.row.UpdatedAt ??
        session?.updatedAt ??
        nowIso(),
      attachedReports: inferredRole === 'user' ? reportNames : [],
      retrievedChunks: [],
      basedOnReports: inferredRole === 'assistant' ? reportNames : [],
    });
  });

  if (messages.length) return finalize(messages);

  // Session list row may already carry Q/A fields from list API
  if (session?.question || session?.answer) {
    pushPair(session.question, session.answer, session.updatedAt, 0);
    return finalize(messages);
  }

  // Last resort: session title as the user question only
  const title = cleanHistoryTitle(session?.displayTitle || session?.sessionTitle || '');
  if (title && title !== 'Previous chat') {
    pushPair(title, null, session?.updatedAt, 0);
  }

  return finalize(messages);
};

const mapHistoryRowsToSessions = (rows) => {
  const bySession = new Map();

  rows.forEach((row) => {
    const sessionId = row.session_id ?? row.SessionID ?? row.sessionId;
    if (sessionId == null) return;
    const key = String(sessionId);
    const existing = bySession.get(key) || {
      sessionId,
      documentId: pickHistoryDocumentId(row),
      documentIds: [],
      sessionTitle: row.session_title ?? row.SessionTitle ?? row.sessionTitle ?? 'Chat',
      updatedAt: row.updated_at ?? row.UpdatedAt ?? row.updatedAt ?? row.created_at ?? row.CreatedAt ?? null,
      question: null,
      answer: null,
      exchanges: [],
    };

    const rowDocId = pickHistoryDocumentId(row);
    const rowDocName = pickHistoryDocumentName(row);
    existing.documentId = existing.documentId ?? rowDocId;
    if (rowDocId) {
      if (!Array.isArray(existing.documentIds)) existing.documentIds = [];
      if (!existing.documentIds.some((id) => Number(id) === Number(rowDocId))) {
        existing.documentIds.push(rowDocId);
      }
    }
    existing.sessionTitle =
      row.session_title ?? row.SessionTitle ?? row.sessionTitle ?? existing.sessionTitle;
    existing.updatedAt =
      row.updated_at ?? row.UpdatedAt ?? row.updatedAt ?? existing.updatedAt;

    const question = pickHistoryQuestion(row);
    const answer = pickHistoryAnswer(row);
    if (question || answer) {
      existing.exchanges.push({
        question,
        answer,
        documentId: rowDocId,
        documentName: parsePrefixedAskQuestion(question || '').reportName || rowDocName,
        createdOn: row.created_at ?? row.CreatedAt ?? row.updated_at ?? row.UpdatedAt ?? existing.updatedAt,
      });
      if (!existing.question && question) existing.question = question;
      if (!existing.answer && answer) existing.answer = answer;
    }

    bySession.set(key, existing);
  });

  return Array.from(bySession.values()).sort((a, b) =>
    String(b.updatedAt || '').localeCompare(String(a.updatedAt || ''))
  );
};

/** Prefer Message/message from API payload only (no hardcoded fallback text). */
const pickApiMessage = (payload) => {
  if (payload == null) return '';
  if (typeof payload === 'string') return payload;
  if (typeof payload !== 'object') return String(payload);

  const fromObj = (obj) => {
    if (!obj || typeof obj !== 'object') return '';
    return (
      obj.Message ??
      obj.message ??
      obj.ErrorMessage ??
      obj.errorMessage ??
      obj.msg ??
      ''
    );
  };

  const direct = fromObj(payload);
  if (direct) return String(direct);

  if (Array.isArray(payload.Table) && payload.Table[0]) {
    const nested = fromObj(payload.Table[0]);
    if (nested) return String(nested);
  }
  if (payload.data && typeof payload.data === 'object') {
    const nested = fromObj(payload.data);
    if (nested) return String(nested);
  }
  return '';
};

const clearSessionMsgs = (userId, sessionId) => {
  try {
    localStorage.removeItem(`${MSGS_STORE_PREFIX}${userId}_${sessionId}`);
  } catch {
    /* ignore */
  }
};

const readHistoryMeta = (userId) => {
  try {
    const raw = localStorage.getItem(`${HISTORY_META_PREFIX}${userId}`);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
};

const writeHistoryMeta = (userId, meta) => {
  localStorage.setItem(`${HISTORY_META_PREFIX}${userId}`, JSON.stringify(meta || {}));
};

const HistoryMenuIcon = ({ type }) => {
  const common = { width: 16, height: 16, viewBox: '0 0 24 24', fill: 'none', 'aria-hidden': true };
  if (type === 'share') {
    return (
      <svg {...common}>
        <path d="M12 3v10M8 7l4-4 4 4" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M5 12v7a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-7" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
      </svg>
    );
  }
  if (type === 'rename') {
    return (
      <svg {...common}>
        <path d="M4 16l8-8 4 4-8 8H4v-4Z" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" />
        <path d="M12 6l2-2 4 4-2 2" stroke="currentColor" strokeWidth="1.7" />
      </svg>
    );
  }
  if (type === 'pin') {
    return (
      <svg {...common}>
        <path d="M15 4l5 5-3 1-4 4-2-2 4-4 1-4Z" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" />
        <path d="M10 14L5 21" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
      </svg>
    );
  }
  if (type === 'archive') {
    return (
      <svg {...common}>
        <path d="M4 7h16v3H4V7Z" stroke="currentColor" strokeWidth="1.7" />
        <path d="M6 10v9h12v-9M10 14h4" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
      </svg>
    );
  }
  return (
    <svg {...common}>
      <path d="M5 7h14M9 7V5h6v2M8 7l1 12h6l1-12" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
};

const readSessionMsgs = (userId, sessionId) => {
  if (userId == null || sessionId == null) return [];
  try {
    const raw = localStorage.getItem(`${MSGS_STORE_PREFIX}${userId}_${String(sessionId)}`);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const writeSessionMsgs = (userId, sessionId, messages) => {
  if (userId == null || sessionId == null) return;
  localStorage.setItem(
    `${MSGS_STORE_PREFIX}${userId}_${String(sessionId)}`,
    JSON.stringify(messages)
  );
};

const readDocMap = (userId) => {
  try {
    const raw = localStorage.getItem(`${DOC_MAP_PREFIX}${userId}`);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
};

const writeDocMap = (userId, map) => {
  localStorage.setItem(`${DOC_MAP_PREFIX}${userId}`, JSON.stringify(map));
};

const parseUploadPayload = (resp) => {
  const root = resp?.data ?? resp ?? {};
  const candidates = [
    root,
    Array.isArray(root) ? root[0] : null,
    Array.isArray(root?.Table) ? root.Table[0] : null,
    Array.isArray(root?.Table1) ? root.Table1[0] : null,
    root?.Result,
    root?.result,
    root?.Data,
    root?.data,
  ].filter(Boolean);

  const pickId = (obj) => {
    if (obj == null || typeof obj !== 'object') return null;
    const raw =
      obj.DocumentID ??
      obj.DocumentId ??
      obj.documentId ??
      obj.document_id ??
      obj.DocID ??
      obj.DocId ??
      obj.docId ??
      null;
    if (raw == null || raw === '') return null;
    const num = Number(raw);
    return Number.isFinite(num) && num > 0 ? num : null;
  };

  let documentId = null;
  let message = '';
  let embeddingStatus = '';
  let documentName = '';

  for (const item of candidates) {
    if (!documentId) documentId = pickId(item);
    if (!message) message = item.Message ?? item.message ?? '';
    if (!embeddingStatus) embeddingStatus = item.EmbeddingStatus ?? item.embeddingStatus ?? '';
    if (!documentName) documentName = item.DocumentName ?? item.documentName ?? '';
  }

  // Deep fallback: search any nested DocumentID-like key
  if (documentId == null) {
    const visit = (node, depth = 0) => {
      if (node == null || depth > 5) return null;
      if (Array.isArray(node)) {
        for (const entry of node) {
          const found = visit(entry, depth + 1);
          if (found != null) return found;
        }
        return null;
      }
      if (typeof node !== 'object') return null;
      const direct = pickId(node);
      if (direct != null) return direct;
      for (const value of Object.values(node)) {
        if (value && typeof value === 'object') {
          const found = visit(value, depth + 1);
          if (found != null) return found;
        }
      }
      return null;
    };
    documentId = visit(root);
  }

  return {
    documentId,
    message: message || (typeof root === 'string' ? root : ''),
    embeddingStatus,
    documentName,
    raw: root,
  };
};

const parseAskPayload = (resp) => {
  const payload = Array.isArray(resp?.Table) ? resp.Table[0] : (resp?.data || resp || {});
  const rawChunks =
    payload.RetrievedChunks ?? payload.retrievedChunks ?? payload.retrieved_chunks ?? [];
  return {
    sessionId: payload.SessionID ?? payload.session_id ?? payload.sessionId ?? null,
    answer: payload.Answer ?? payload.answer ?? '',
    chunks: Array.isArray(rawChunks) ? rawChunks : [],
    documentId: payload.DocumentID ?? payload.document_id ?? payload.documentId ?? null,
  };
};

const isUnsupportedImageReport = (reportName, blob) => {
  const name = String(reportName || '');
  const type = String(blob?.type || '').toLowerCase();
  return (
    /\.(jpe?g|png|jfif)$/i.test(name) ||
    type.includes('png') ||
    type.includes('jpeg') ||
    type.includes('jpg') ||
    type.includes('jfif')
  );
};

const INDEX_READY_STATUS = /^(ready|completed|success|processed)$/i;
const INDEX_FAILED_STATUS = /^(failed|rejected|notext|nochunks)$/i;

const isIndexReady = (uploaded) =>
  uploaded?.documentId != null &&
  (!uploaded.embeddingStatus || INDEX_READY_STATUS.test(String(uploaded.embeddingStatus)));

const askAcmePrepareErrorMessage = (reportName, blob, apiMessage) => {
  const serverMessage = String(apiMessage || '').trim();
  if (serverMessage) return serverMessage;
  const name = reportName || 'this file';
  if (isUnsupportedImageReport(name, blob)) {
    return `Ask Acme does not support JPG/PNG formats. We couldn't prepare "${name}" for questions. Please try again with a PDF report.`;
  }
  return `We couldn't prepare "${name}" for questions. Please try again with a PDF report.`;
};

const buildUploadFileName = (reportName, reportId, blob) => {
  let name = (reportName || `report-${reportId}`).replace(/[\\/:*?"<>|]+/g, '_').trim();
  if (/\.(pdf|png|jpe?g|docx?|txt|jfif)$/i.test(name)) return name;
  const type = (blob?.type || '').toLowerCase();
  if (type.includes('png')) return `${name}.png`;
  if (type.includes('jpeg') || type.includes('jpg') || type.includes('jfif')) return `${name}.jpg`;
  if (type.includes('word')) return `${name}.docx`;
  if (type.includes('pdf')) return `${name}.pdf`;
  return `${name}.pdf`;
};

const formatShortDate = (value) => {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value).slice(0, 10);
  return d.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' });
};

const AskAcme = ({ selectedFamilyMember }) => {
  const dispatch = useDispatch();
  const token = useSelector((state) => state.apiService.token);
  const login = useSelector((state) => state.apiService.login);
  const userId =
    selectedFamilyMember?.user_id ||
    selectedFamilyMember?.UserId ||
    login?.user_id ||
    login?.UserId ||
    login?.UserID;
  const usersDetailsGet = useSelector((state) => state.apiService.usersDetailsGet);
  const [reports, setReports] = useState([]);
  const [askDocuments, setAskDocuments] = useState([]); // indexed uploads (DocumentID ready)
  const [selectedReportIds, setSelectedReportIds] = useState([]);
  const [selectedAskDocIds, setSelectedAskDocIds] = useState([]);
  /** Documents used in the current chat — reused for follow-up questions */
  const [sessionDocs, setSessionDocs] = useState([]); // [{ documentId, documentName }]
  const [openCategories, setOpenCategories] = useState([]);
  const [historyExpanded, setHistoryExpanded] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [activeSessionId, setActiveSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadingFileName, setUploadingFileName] = useState('');
  const [filingNotice, setFilingNotice] = useState(null);
  const [nowTs, setNowTs] = useState(() => Date.now());
  const [recentUploads, setRecentUploads] = useState({});
  const [awaitingVaultReport, setAwaitingVaultReport] = useState(false);
  const [isLoadingReports, setIsLoadingReports] = useState(false);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [deletingSessionId, setDeletingSessionId] = useState(null);
  const [deleteConfirmSession, setDeleteConfirmSession] = useState(null);
  const [showDeleteSuccessModal, setShowDeleteSuccessModal] = useState(false);
  const [deleteSuccessMessage, setDeleteSuccessMessage] = useState('');
  const [showComingSoonModal, setShowComingSoonModal] = useState(false);
  const [historyMenuId, setHistoryMenuId] = useState(null);
  const [historyMenuPos, setHistoryMenuPos] = useState(null);
  const [historyMeta, setHistoryMeta] = useState({});
  const [renamingSessionId, setRenamingSessionId] = useState(null);
  const [renameValue, setRenameValue] = useState('');
  const [errorBanner, setErrorBanner] = useState('');

  const clearErrorBanner = useCallback(() => {
    setErrorBanner((message) => {
      if (/couldn't prepare|JPG\/PNG/i.test(String(message || ''))) {
        setSelectedReportIds([]);
        setSelectedAskDocIds([]);
      }
      return '';
    });
  }, []);

  const chatBodyRef = useRef(null);
  const inputRef = useRef(null);
  const fileInputRef = useRef(null);
  const historyMenuRef = useRef(null);
  const renameInputRef = useRef(null);
  const renameCommitLockRef = useRef(false);
  const sendAbortRef = useRef(null);
  const sendGenerationRef = useRef(0);
  const activeSessionIdRef = useRef(null);
  const historyLoadGenerationRef = useRef(0);
  const skipNextUploadsWriteRef = useRef(false);
  const shownFiledToastRef = useRef(new Set());
  const pendingVaultUploadRef = useRef(null);

  useEffect(() => {
    if (!userId) {
      setHistoryMeta({});
      return;
    }
    setHistoryMeta(readHistoryMeta(userId));
  }, [userId]);

  useEffect(() => {
    activeSessionIdRef.current = activeSessionId;
  }, [activeSessionId]);

  useEffect(() => {
    if (renamingSessionId != null) {
      renameInputRef.current?.focus();
      renameInputRef.current?.select();
    }
  }, [renamingSessionId]);

  useEffect(() => {
    if (!historyMenuId) return undefined;
    const onPointerDown = (event) => {
      if (event.target.closest?.('.ask-acme-history-more')) return;
      if (historyMenuRef.current && !historyMenuRef.current.contains(event.target)) {
        setHistoryMenuId(null);
        setHistoryMenuPos(null);
      }
    };
    const onRepositionClose = () => {
      setHistoryMenuId(null);
      setHistoryMenuPos(null);
    };
    document.addEventListener('mousedown', onPointerDown);
    window.addEventListener('resize', onRepositionClose);
    window.addEventListener('scroll', onRepositionClose, true);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      window.removeEventListener('resize', onRepositionClose);
      window.removeEventListener('scroll', onRepositionClose, true);
    };
  }, [historyMenuId]);

  const updateHistoryMeta = useCallback(
    (sessionId, patch) => {
      if (!userId || sessionId == null) return;
      setHistoryMeta((prev) => {
        const key = String(sessionId);
        const next = {
          ...prev,
          [key]: {
            ...(prev[key] || {}),
            ...patch,
          },
        };
        writeHistoryMeta(userId, next);
        return next;
      });
    },
    [userId]
  );

  const visibleSessions = useMemo(() => {
    const list = Array.isArray(sessions) ? sessions : [];
    return list
      .map((session) => {
        const meta = historyMeta[String(session.sessionId)] || {};
        return {
          ...session,
          displayTitle: meta.title || session.sessionTitle || 'Chat',
        };
      })
      .sort((a, b) => String(b.updatedAt || '').localeCompare(String(a.updatedAt || '')));
  }, [sessions, historyMeta]);

  const HISTORY_VISIBLE_ROWS = 3;
  const historyHasMore = visibleSessions.length > HISTORY_VISIBLE_ROWS;

  const selectedReports = useMemo(
    () => (Array.isArray(reports) ? reports : []).filter((r) => selectedReportIds.includes(r.reportId)),
    [reports, selectedReportIds]
  );

  const selectedAskDocuments = useMemo(
    () =>
      (Array.isArray(askDocuments) ? askDocuments : []).filter((d) =>
        selectedAskDocIds.includes(d.documentId)
      ),
    [askDocuments, selectedAskDocIds]
  );

  /** Share Everywhere–style categories derived from Your Reports group_name */
  const reportCategories = useMemo(() => {
    const map = new Map();
    (Array.isArray(reports) ? reports : []).forEach((r) => {
      const name = (r.groupName || 'General').trim() || 'General';
      if (!map.has(name)) map.set(name, []);
      map.get(name).push(r);
    });
    return Array.from(map.entries()).map(([name, items]) => ({ name, items }));
  }, [reports]);

  const visibleReportIds = useMemo(
    () => (Array.isArray(reports) ? reports : []).map((r) => r.reportId),
    [reports]
  );

  const visibleAskDocIds = useMemo(
    () => (Array.isArray(askDocuments) ? askDocuments : []).map((d) => d.documentId),
    [askDocuments]
  );

  const visibleItemCount = visibleReportIds.length + visibleAskDocIds.length;

  const selectedVisibleCount = useMemo(() => {
    const reportCount = visibleReportIds.filter((id) => selectedReportIds.includes(id)).length;
    const askCount = visibleAskDocIds.filter((id) => selectedAskDocIds.includes(id)).length;
    return reportCount + askCount;
  }, [visibleReportIds, visibleAskDocIds, selectedReportIds, selectedAskDocIds]);

  const allVisibleSelected = visibleItemCount > 0 && selectedVisibleCount === visibleItemCount;
  const someVisibleSelected = selectedVisibleCount > 0 && !allVisibleSelected;

  const uploadsOpen = openCategories.includes('__uploads__');
  const uploadsSelection = getCategorySelectionState(visibleAskDocIds, selectedAskDocIds);

  const isCategoryOpen = useCallback(
    (categoryKey) => openCategories.includes(categoryKey),
    [openCategories]
  );

  const scrollToBottom = useCallback(() => {
    if (chatBodyRef.current) {
      chatBodyRef.current.scrollTop = chatBodyRef.current.scrollHeight;
    }
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, isSending, scrollToBottom]);

  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    const prevHtmlOverflow = html.style.overflow;
    const prevBodyOverflow = body.style.overflow;
    html.style.overflow = 'hidden';
    body.style.overflow = 'hidden';
    return () => {
      html.style.overflow = prevHtmlOverflow;
      body.style.overflow = prevBodyOverflow;
    };
  }, []);

  const loadReports = useCallback(async (options = {}) => {
    if (!userId || !token) return [];
    const silent = Boolean(options.silent);
    if (!silent) setIsLoadingReports(true);
    try {
      const resp = await dispatch(
        getMRSitesReports({
          user_id: userId,
          token,
          SearchKey: '',
          ReportGroupName: '',
          ProviderName: '',
          HospitalName: '',
          ReportDateStart: '',
          ReportDateEnd: '',
          SiteID: 0,
          IncludeArchived: false,
          ReportID: 0,
        })
      ).unwrap();
      const rows = unwrapRows(resp);
      const mapped = rows
        .filter((r) => !r.is_archived)
        .map((r) => ({
          reportId: Number(r.report_id ?? r.ReportID ?? r.reportId),
          reportName: r.report_name ?? r.ReportName ?? r.edit_report_name ?? 'Untitled report',
          reportDate: r.report_date ?? r.ReportDate ?? '',
          groupName: r.group_name ?? r.GroupName ?? '',
          hospitalName: r.hospital_name ?? r.HospitalName ?? '',
          providerName: r.provider_name ?? r.ProviderName ?? '',
          reportPath: r.report_path ?? r.ReportPath ?? r.url ?? '',
        }))
        .filter((r) => r.reportId);
      setReports(mapped);
      return mapped;
    } catch (err) {
      console.error('Your Reports load failed', err);
      if (!silent) setErrorBanner('Could not load Your Reports. Please try again.');
      if (!silent) setReports([]);
      return [];
    } finally {
      if (!silent) setIsLoadingReports(false);
    }
  }, [dispatch, token, userId]);

  const loadHistory = useCallback(async () => {
    if (!userId || !token) return;
    setIsLoadingHistory(true);
    try {
      const resp = await dispatch(
        documentAssistantChatHistoryGet({ UserId: userId, token })
      ).unwrap();
      const rows = unwrapHistoryRows(resp);
      setSessions(mapHistoryRowsToSessions(Array.isArray(rows) ? rows : []));
    } catch (err) {
      console.error('DocumentAssistantChatHistoryGet failed', err);
      setSessions([]);
    } finally {
      setIsLoadingHistory(false);
    }
  }, [dispatch, token, userId]);

  // Restore Ask ACME uploads (session + refresh) from local metadata
  useEffect(() => {
    if (!userId) {
      setAskDocuments([]);
      skipNextUploadsWriteRef.current = false;
      return;
    }
    skipNextUploadsWriteRef.current = true;
    shownFiledToastRef.current = new Set();
    const meta = readUploadsMeta(userId);
    const restored = meta
      .filter((d) => d?.documentId != null)
      .map((d) => ({
        documentId: Number(d.documentId),
        documentName: d.documentName || d.fileName || 'Untitled',
        embeddingStatus: d.embeddingStatus || 'Ready',
        fileName: d.fileName || d.documentName || 'file',
        fileType: d.fileType || '',
        file: null,
        uploadedAt: d.uploadedAt || null,
        baselineReportIds: Array.isArray(d.baselineReportIds) ? d.baselineReportIds : [],
        filedGroupName: d.filedGroupName || null,
        filedReportId: d.filedReportId ?? null,
      }));
    restored.forEach((d) => {
      if (d.filedGroupName) shownFiledToastRef.current.add(d.documentId);
    });
    setAskDocuments(restored);
    if (restored.length > 0) {
      setOpenCategories((prev) => (prev.includes('__uploads__') ? prev : [...prev, '__uploads__']));
    }
  }, [userId]);

  useEffect(() => {
    if (!userId) return;
    if (skipNextUploadsWriteRef.current) {
      skipNextUploadsWriteRef.current = false;
      return;
    }
    writeUploadsMeta(userId, askDocuments);
  }, [userId, askDocuments]);

  useEffect(() => {
    if (!userId) return;
    setSelectedReportIds([]);
    setSelectedAskDocIds([]);
    setSessionDocs([]);
    setActiveSessionId(null);
    setMessages([]);
    setInputValue('');
    setErrorBanner('');
    setFilingNotice(null);
    setRecentUploads({});
    setAwaitingVaultReport(false);
    pendingVaultUploadRef.current = null;
    setHistoryMenuId(null);
    setHistoryMenuPos(null);
    setRenamingSessionId(null);
  }, [userId]);

  useEffect(() => {
    if (!userId || !token) return;
    loadReports();
    loadHistory();
  }, [userId, token, loadReports, loadHistory]);

  const markRecentUpload = useCallback((report, uploadedAt) => {
    if (!report?.reportId) return;
    const group = (report.groupName || 'General').trim() || 'General';
    setRecentUploads((prev) => ({
      ...prev,
      [report.reportId]: { uploadedAt, startGroup: group },
    }));
    setSelectedReportIds((prev) =>
      prev.includes(report.reportId) ? prev : [...prev, report.reportId]
    );
    setOpenCategories((prev) => (prev.includes(group) ? prev : [...prev, group]));
    setAwaitingVaultReport(false);
  }, []);

  const pendingFiling = useMemo(() => {
    if (awaitingVaultReport) {
      const start = Date.parse(pendingVaultUploadRef.current?.uploadedAt || '');
      if (!Number.isFinite(start) || Date.now() - start < REPORT_FILE_WATCH_MS) return true;
    }
    const askPending = (Array.isArray(askDocuments) ? askDocuments : []).some((d) => {
      if (d.filedGroupName || !d.uploadedAt) return false;
      const start = Date.parse(d.uploadedAt);
      return Number.isFinite(start) && Date.now() - start < REPORT_FILE_WATCH_MS;
    });
    if (askPending) return true;
    const list = Array.isArray(reports) ? reports : [];
    return Object.entries(recentUploads).some(([id, meta]) => {
      if (meta.filedGroup) return false;
      const start = Date.parse(meta.uploadedAt || '');
      if (!Number.isFinite(start) || Date.now() - start >= REPORT_FILE_WATCH_MS) return false;
      const report = list.find((r) => String(r.reportId) === String(id));
      const group = (report?.groupName || 'General').trim() || 'General';
      return group === (meta.startGroup || 'General');
    });
  }, [askDocuments, awaitingVaultReport, recentUploads, reports, nowTs]);

  useEffect(() => {
    if (!filingNotice) return undefined;
    const timer = window.setTimeout(() => setFilingNotice(null), FILING_NOTICE_MS);
    return () => window.clearTimeout(timer);
  }, [filingNotice]);

  useEffect(() => {
    if (!pendingFiling) return undefined;
    setNowTs(Date.now());
    const tick = window.setInterval(() => setNowTs(Date.now()), 15000);
    return () => window.clearInterval(tick);
  }, [pendingFiling]);

  useEffect(() => {
    if (!pendingFiling || !userId || !token) return undefined;
    loadReports({ silent: true });
    const poll = window.setInterval(() => {
      loadReports({ silent: true });
    }, REPORT_FILE_POLL_MS);
    return () => window.clearInterval(poll);
  }, [pendingFiling, userId, token, loadReports]);

  useEffect(() => {
    const reportList = Array.isArray(reports) ? reports : [];
    if (!reportList.length) return;
    setAskDocuments((prev) => {
      let changed = false;
      const next = prev.map((doc) => {
        if (doc.filedGroupName || !doc.uploadedAt) return doc;
        const key = normalizeReportKey(doc.documentName || doc.fileName);
        if (!key) return doc;
        const baseline = new Set((doc.baselineReportIds || []).map(String));
        const match = reportList.find((report) => {
          if (baseline.has(String(report.reportId))) return false;
          return normalizeReportKey(report.reportName) === key;
        });
        if (!match) return doc;
        changed = true;
        return {
          ...doc,
          filedGroupName: (match.groupName || 'General').trim() || 'General',
          filedReportId: match.reportId,
        };
      });
      return changed ? next : prev;
    });
  }, [reports]);

  useEffect(() => {
    const newlyFiled = (Array.isArray(askDocuments) ? askDocuments : []).filter(
      (doc) => doc.filedGroupName && !shownFiledToastRef.current.has(doc.documentId)
    );
    if (!newlyFiled.length) return;
    newlyFiled.forEach((doc) => shownFiledToastRef.current.add(doc.documentId));
    const first = newlyFiled[0];
    setOpenCategories((prev) =>
      prev.includes(first.filedGroupName) ? prev : [...prev, first.filedGroupName]
    );
    setFilingNotice({
      title: `"${first.documentName}" is now in ${first.filedGroupName}`,
      body: 'You can find it under Your Reports categories.',
    });
  }, [askDocuments]);

  useEffect(() => {
    const pending = pendingVaultUploadRef.current;
    if (!pending) return;
    const match = findNewSavedReport(reports, pending);
    if (!match) return;
    pendingVaultUploadRef.current = null;
    markRecentUpload(match, pending.uploadedAt);
  }, [reports, markRecentUpload]);

  useEffect(() => {
    const list = Array.isArray(reports) ? reports : [];
    setRecentUploads((prev) => {
      let changed = false;
      const next = { ...prev };
      Object.keys(next).forEach((id) => {
        const meta = next[id];
        if (meta.filedGroup) return;
        const report = list.find((r) => String(r.reportId) === String(id));
        if (!report) return;
        const group = (report.groupName || 'General').trim() || 'General';
        if (group !== (meta.startGroup || 'General')) {
          next[id] = { ...meta, filedGroup: group };
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [reports]);

  useEffect(() => {
    const list = Array.isArray(reports) ? reports : [];
    Object.entries(recentUploads).forEach(([id, meta]) => {
      if (!meta.filedGroup) return;
      const toastKey = `report-${id}`;
      if (shownFiledToastRef.current.has(toastKey)) return;
      shownFiledToastRef.current.add(toastKey);
      const report = list.find((r) => String(r.reportId) === String(id));
      setOpenCategories((prev) =>
        prev.includes(meta.filedGroup) ? prev : [...prev, meta.filedGroup]
      );
      setFilingNotice({
        title: `"${report?.reportName || 'Report'}" is now in ${meta.filedGroup}`,
        body: 'You can find it under Your Reports categories.',
      });
    });
  }, [recentUploads, reports]);

  const abortInFlightSend = useCallback(() => {
    sendGenerationRef.current += 1;
    try {
      sendAbortRef.current?.abort();
    } catch (_) {
      /* ignore */
    }
    sendAbortRef.current = null;
    setIsSending(false);
  }, []);

  useEffect(() => {
    const resyncActiveSession = () => {
      if (document.visibilityState !== 'visible') return;
      if (isSending || userId == null || activeSessionIdRef.current == null) return;
      const localMsgs = mergeSplitMultiReportMessages(
        readSessionMsgs(userId, activeSessionIdRef.current)
      );
      if (localMsgs.length) setMessages(localMsgs);
    };
    document.addEventListener('visibilitychange', resyncActiveSession);
    return () => document.removeEventListener('visibilitychange', resyncActiveSession);
  }, [userId, isSending]);

  const handleStopSend = useCallback(() => {
    abortInFlightSend();
    setErrorBanner('');
    inputRef.current?.focus();
  }, [abortInFlightSend]);

  const handleNewChat = () => {
    // Stop any in-progress search/indexing, then reset to a blank chat.
    abortInFlightSend();
    setActiveSessionId(null);
    setMessages([]);
    setSessionDocs([]);
    setInputValue('');
    setErrorBanner('');
    setHistoryMenuId(null);
    setHistoryMenuPos(null);
    setRenamingSessionId(null);
    inputRef.current?.focus();
  };

  const handleToggleReport = (reportId) => {
    setSelectedReportIds((prev) =>
      prev.includes(reportId) ? prev.filter((id) => id !== reportId) : [...prev, reportId]
    );
  };

  const handleToggleAskDocument = (documentId) => {
    setSelectedAskDocIds((prev) =>
      prev.includes(documentId) ? prev.filter((id) => id !== documentId) : [...prev, documentId]
    );
  };

  const handleToggleSelectAllVisible = () => {
    if (allVisibleSelected) {
      setSelectedReportIds((prev) => prev.filter((id) => !visibleReportIds.includes(id)));
      setSelectedAskDocIds((prev) => prev.filter((id) => !visibleAskDocIds.includes(id)));
      return;
    }
    setSelectedReportIds((prev) => Array.from(new Set([...prev, ...visibleReportIds])));
    setSelectedAskDocIds((prev) => Array.from(new Set([...prev, ...visibleAskDocIds])));
  };

  const toggleCategoryExpand = (categoryKey) => {
    setOpenCategories((prev) =>
      prev.includes(categoryKey) ? prev.filter((k) => k !== categoryKey) : [...prev, categoryKey]
    );
  };

  const handleToggleCategoryReports = (categoryReportIds) => {
    const { all } = getCategorySelectionState(categoryReportIds, selectedReportIds);
    if (all) {
      setSelectedReportIds((prev) => prev.filter((id) => !categoryReportIds.includes(id)));
      return;
    }
    setSelectedReportIds((prev) => Array.from(new Set([...prev, ...categoryReportIds])));
  };

  const handleToggleUploadsCategory = () => {
    const { all } = getCategorySelectionState(visibleAskDocIds, selectedAskDocIds);
    if (all) {
      setSelectedAskDocIds((prev) => prev.filter((id) => !visibleAskDocIds.includes(id)));
      return;
    }
    setSelectedAskDocIds((prev) => Array.from(new Set([...prev, ...visibleAskDocIds])));
  };

  const handleUploadClick = () => fileInputRef.current?.click();

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!userId || !token) {
      setErrorBanner('User session not ready. Please refresh and try again.');
      return;
    }

    setIsUploading(true);
    setUploadingFileName(file.name);
    setErrorBanner('');
    try {
      const reportName = file.name.replace(/\.[^.]+$/, '') || file.name;
      const baselineIds = (Array.isArray(reports) ? reports : []).map((r) => r.reportId);

      const formData = new FormData();
      formData.append('chunk', file);
      formData.append('Index', '0');
      formData.append('TotalChunks', '1');
      formData.append('FileName', file.name);
      formData.append('GroupID', '0');
      formData.append('TagNo', reportName);
      formData.append('ProviderName', '');
      formData.append('HospitalName', '');
      formData.append('UserID', userId);
      formData.append('SiteID', '0');
      formData.append('ReportDate', formatDateToYYYYMMDD(new Date()));
      formData.append('token', token);

      const resultAction = await dispatch(saveReportDocument(formData));
      if (!saveReportDocument.fulfilled.match(resultAction)) {
        throw new Error(
          getSaveReportError(resultAction.payload, resultAction.error?.message)
        );
      }

      const payload = resultAction.payload;
      const returnMessage = payload?.Return_Message ?? payload?.return_message ?? '';
      if (/already exists/i.test(String(returnMessage))) {
        throw new Error(returnMessage || 'A report with this name already exists.');
      }

      const pending = {
        name: reportName,
        uploadedAt: nowIso(),
        baselineIds,
        reportId: parseSavedReportId(payload),
      };
      pendingVaultUploadRef.current = pending;
      setAwaitingVaultReport(true);
      setNowTs(Date.now());

      const mapped = await loadReports({ silent: true });
      const match = findNewSavedReport(mapped, pending);
      if (match) {
        pendingVaultUploadRef.current = null;
        markRecentUpload(match, pending.uploadedAt);
      }

      const groupName = (match?.groupName || 'General').trim() || 'General';
      setFilingNotice({
        title: `"${file.name}" uploaded`,
        body: `Saved to Your Reports → ${groupName}. Category may update in about 5 minutes.`,
      });
    } catch (err) {
      console.error('Your Reports upload failed', err);
      pendingVaultUploadRef.current = null;
      setAwaitingVaultReport(false);
      setErrorBanner(
        (typeof err === 'string' && err) ||
        err?.message ||
        'Document upload failed.'
      );
    } finally {
      setIsUploading(false);
      setUploadingFileName('');
    }
  };

  const handleSelectHistory = async (session) => {
    const sessionId = session.sessionId;
    const loadGeneration = ++historyLoadGenerationRef.current;

    abortInFlightSend();
    closeHistoryMenu();
    setRenamingSessionId(null);
    setActiveSessionId(sessionId);
    setSelectedReportIds([]);
    setSelectedAskDocIds([]);
    setErrorBanner('');

    const meta = historyMeta[String(sessionId)] || {};
    const metaDocs = uniqueDocs(meta.sessionDocs);
    const listDocs = uniqueDocs([
      ...(Array.isArray(session.documentIds) ? session.documentIds.map((id) => ({ documentId: id })) : []),
      session.documentId != null ? { documentId: session.documentId, documentName: session.sessionTitle } : null,
      ...(Array.isArray(session.exchanges)
        ? session.exchanges.map((ex) => ({
          documentId: ex.documentId,
          documentName: ex.documentName,
        }))
        : []),
    ]);

    // Restore cached thread immediately so the UI never shows the previous chat.
    const localMsgs = mergeSplitMultiReportMessages(readSessionMsgs(userId, sessionId));
    setMessages(localMsgs);

    let apiMessages = mergeSplitMultiReportMessages(
      buildMessagesFromHistoryApi(
        { Table: session.exchanges?.length ? session.exchanges : null },
        session
      )
    );

    let apiDocs = [];
    // Fetch the full session thread (do not pass DocumentID — that filters to one report).
    if (userId && token) {
      try {
        const resp = await dispatch(
          documentAssistantChatHistoryGet({
            token,
            UserId: userId,
            sessionId: sessionId,
          })
        ).unwrap();
        if (loadGeneration !== historyLoadGenerationRef.current) return;
        apiDocs = collectDocumentsFromHistoryRows(unwrapHistoryRows(resp));
        const detailed = mergeSplitMultiReportMessages(buildMessagesFromHistoryApi(resp, session));
        if (detailed.length) apiMessages = detailed;
      } catch (err) {
        console.error('DocumentAssistantChatHistoryGet (session) failed', err);
      }
    }

    if (loadGeneration !== historyLoadGenerationRef.current) return;

    const namesFromMsgs = [];
    [...localMsgs, ...apiMessages].forEach((msg) => {
      [...(msg.attachedReports || []), ...(msg.basedOnReports || [])].forEach((name) => {
        if (name) namesFromMsgs.push(name);
      });
    });
    const cache = readDocMap(userId);
    const namedDocs = namesFromMsgs.map((name) => {
      const report = (Array.isArray(reports) ? reports : []).find(
        (r) => String(r.reportName) === String(name)
      );
      if (report && cache[report.reportId]) {
        return { documentId: Number(cache[report.reportId]), documentName: name };
      }
      const upload = (Array.isArray(askDocuments) ? askDocuments : []).find(
        (d) => String(d.documentName) === String(name)
      );
      if (upload?.documentId) {
        return { documentId: Number(upload.documentId), documentName: name };
      }
      return null;
    });

    const restoredDocs = uniqueDocs([...metaDocs, ...listDocs, ...apiDocs, ...namedDocs]);
    setSessionDocs(restoredDocs);
    if (restoredDocs.length) {
      updateHistoryMeta(sessionId, { sessionDocs: restoredDocs });
    }

    const bestMessages = preferRicherHistory(localMsgs, apiMessages);
    if (bestMessages.length) {
      writeSessionMsgs(userId, sessionId, bestMessages);
      if (String(activeSessionIdRef.current) === String(sessionId)) {
        setMessages(bestMessages);
      }
      return;
    }

    if (String(activeSessionIdRef.current) === String(sessionId)) {
      setMessages([]);
    }
  };

  const closeHistoryMenu = () => {
    setHistoryMenuId(null);
    setHistoryMenuPos(null);
  };

  const handleOpenHistoryMenu = (event, session) => {
    event.stopPropagation();
    event.preventDefault();
    if (String(historyMenuId) === String(session.sessionId)) {
      closeHistoryMenu();
      return;
    }

    const rect = event.currentTarget.getBoundingClientRect();
    const menuWidth = 160;
    const menuHeight = 132;
    const openUp = window.innerHeight - rect.bottom < menuHeight + 12;
    const top = openUp
      ? Math.max(8, rect.top - menuHeight - 4)
      : Math.min(window.innerHeight - menuHeight - 8, rect.bottom + 4);
    const left = Math.min(
      window.innerWidth - menuWidth - 8,
      Math.max(8, rect.right - menuWidth)
    );

    setHistoryMenuPos({ top, left });
    setHistoryMenuId(session.sessionId);
  };

  const handleShareHistory = (event) => {
    event.stopPropagation();
    event.preventDefault();
    closeHistoryMenu();
    setShowComingSoonModal(true);
  };

  const handleStartRenameHistory = (event, session) => {
    event.stopPropagation();
    event.preventDefault();
    closeHistoryMenu();
    setRenamingSessionId(session.sessionId);
    setRenameValue(session.displayTitle || session.sessionTitle || '');
  };

  const handleCommitRenameHistory = async (session) => {
    if (renameCommitLockRef.current) return;

    const nextTitle = renameValue.trim();
    const previousTitle = (session.displayTitle || session.sessionTitle || '').trim();
    setRenamingSessionId(null);
    if (!nextTitle || nextTitle === previousTitle) return;
    if (!userId || !token || session?.sessionId == null) return;

    renameCommitLockRef.current = true;
    const sessionId = session.sessionId;
    const previousSessions = sessions;
    const previousMeta = historyMeta;
    setErrorBanner('');

    updateHistoryMeta(sessionId, { title: nextTitle });
    setSessions((prev) =>
      prev.map((item) =>
        String(item.sessionId) === String(sessionId)
          ? { ...item, sessionTitle: nextTitle }
          : item
      )
    );

    try {
      const resp = await dispatch(
        documentAssistantChatSessionRename({
          token,
          UserID: userId,
          SessionID: sessionId,
          SessionTitle: nextTitle,
        })
      ).unwrap();

      const apiMessage = pickApiMessage(resp);
      const successFlag = resp?.Success ?? resp?.success;
      const failed =
        successFlag === false ||
        successFlag === 0 ||
        successFlag === 'false';

      if (failed) {
        setSessions(previousSessions);
        setHistoryMeta(previousMeta);
        writeHistoryMeta(userId, previousMeta);
        if (apiMessage) setErrorBanner(apiMessage);
        return;
      }

      await loadHistory();
    } catch (err) {
      setSessions(previousSessions);
      setHistoryMeta(previousMeta);
      writeHistoryMeta(userId, previousMeta);
      const apiMessage = pickApiMessage(err);
      if (apiMessage) setErrorBanner(apiMessage);
    } finally {
      renameCommitLockRef.current = false;
    }
  };

  const handleRequestDeleteHistory = (event, session) => {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }
    closeHistoryMenu();
    setDeleteConfirmSession(session);
  };

  const handleCancelDeleteHistory = () => {
    if (deletingSessionId != null) return;
    setDeleteConfirmSession(null);
  };

  const handleConfirmDeleteHistory = async () => {
    const session = deleteConfirmSession;
    if (!userId || !token || session?.sessionId == null || deletingSessionId != null) return;

    const sessionId = session.sessionId;
    const previousSessions = sessions;
    const previousMeta = historyMeta;
    const wasActive = String(activeSessionId) === String(sessionId);

    setDeletingSessionId(sessionId);
    setErrorBanner('');

    // Fast remove from Recents immediately
    setSessions((prev) => prev.filter((s) => String(s.sessionId) !== String(sessionId)));
    setHistoryMeta((prev) => {
      const next = { ...prev };
      delete next[String(sessionId)];
      writeHistoryMeta(userId, next);
      return next;
    });
    clearSessionMsgs(userId, sessionId);
    if (wasActive) {
      setActiveSessionId(null);
      setMessages([]);
      setInputValue('');
    }
    setDeleteConfirmSession(null);

    try {
      const resp = await dispatch(
        documentAssistantChatHistoryDelete({
          token,
          UserId: userId,
          SessionId: sessionId,
          DocumentID: session.documentId,
        })
      ).unwrap();

      const apiMessage = pickApiMessage(resp);
      const successFlag = resp?.Success ?? resp?.success;
      const failed =
        successFlag === false ||
        successFlag === 0 ||
        successFlag === 'false';

      if (failed) {
        setSessions(previousSessions);
        setHistoryMeta(previousMeta);
        writeHistoryMeta(userId, previousMeta);
        if (apiMessage) setErrorBanner(apiMessage);
        return;
      }

      if (apiMessage) {
        setDeleteSuccessMessage(apiMessage);
        setShowDeleteSuccessModal(true);
      }
      await loadHistory();
    } catch (err) {
      setSessions(previousSessions);
      setHistoryMeta(previousMeta);
      writeHistoryMeta(userId, previousMeta);
      const apiMessage = pickApiMessage(err);
      if (apiMessage) setErrorBanner(apiMessage);
    } finally {
      setDeletingSessionId(null);
    }
  };

  const appendAskExchange = useCallback(
    (sessionId, question, answer, retrievedChunks, attachedReportNames) => {
      const exchange = [
        {
          messageId: `u-${Date.now()}`,
          role: 'user',
          message: question,
          createdOn: nowIso(),
          attachedReports: Array.isArray(attachedReportNames) ? attachedReportNames : [],
        },
        {
          messageId: `a-${Date.now() + 1}`,
          role: 'assistant',
          message: answer,
          createdOn: nowIso(),
          retrievedChunks: Array.isArray(retrievedChunks) ? retrievedChunks : [],
          basedOnReports: Array.isArray(attachedReportNames) ? attachedReportNames : [],
        },
      ];

      if (sessionId == null) {
        setMessages((prev) => [...(Array.isArray(prev) ? prev : []), ...exchange]);
        return;
      }

      const storedBase = readSessionMsgs(userId, sessionId);
      const next = [...(Array.isArray(storedBase) ? storedBase : []), ...exchange];
      writeSessionMsgs(userId, sessionId, next);

      if (String(activeSessionIdRef.current) === String(sessionId)) {
        setMessages(next);
      }
    },
    [userId]
  );

  /**
   * Your Reports report_id ≠ DocumentAssistant DocumentID.
   * Prefer indexing by server filepath (BE contract), then multipart file fallback.
   */
  const ensureIndexedDocumentId = async (report, { forceReindex = false, signal } = {}) => {
    const cache = readDocMap(userId);
    if (!forceReindex && cache[report.reportId]) {
      return Number(cache[report.reportId]);
    }

    const filename = buildUploadFileName(report.reportName, report.reportId, null);
    let uploaded = null;

    // 1) Primary: JSON upload using existing server report path + ReportID
    if (report.reportPath) {
      try {
        const uploadResp = await dispatch(
          documentAssistantUpload({
            token,
            userId,
            filename,
            filepath: report.reportPath,
            ReportID: report.reportId,
            mode: 'json',
            signal,
          })
        ).unwrap();
        uploaded = parseUploadPayload(uploadResp);
      } catch (err) {
        if (err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED' || signal?.aborted) {
          throw err;
        }
        console.warn('DocumentAssistantUpload JSON/path failed, trying file upload', err);
      }
    }

    // 2) Fallback: download report blob and multipart upload (correct boundary)
    if (uploaded?.documentId == null) {
      if (signal?.aborted) {
        const abortErr = new Error('ASK_ACME_ABORTED');
        abortErr.name = 'AbortError';
        throw abortErr;
      }
      const blob = await dispatch(
        mrSitesReportsGetByReportID({
          ReportID: report.reportId,
          UserID: userId,
          token,
        })
      ).unwrap();

      if (!(blob instanceof Blob) || blob.size === 0) {
        throw new Error(askAcmePrepareErrorMessage(report.reportName));
      }

      const fileName = buildUploadFileName(report.reportName, report.reportId, blob);
      const file = new File([blob], fileName, { type: blob.type || 'application/pdf' });

      try {
        const uploadResp = await dispatch(
          documentAssistantUpload({
            token,
            userId,
            filename: fileName,
            filepath: report.reportPath || fileName,
            ReportID: report.reportId,
            file,
            mode: 'multipart',
            signal,
          })
        ).unwrap();
        uploaded = parseUploadPayload(uploadResp);
      } catch (err) {
        if (err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED' || signal?.aborted) {
          throw err;
        }
        throw new Error(askAcmePrepareErrorMessage(fileName || report.reportName, file));
      }
    }

    if (uploaded?.documentId == null) {
      throw new Error(askAcmePrepareErrorMessage(report.reportName));
    }

    const documentId = Number(uploaded.documentId);
    cache[report.reportId] = documentId;
    writeDocMap(userId, cache);
    return documentId;
  };

  const askWithDocument = async (documentId, question, reportName, signal, sessionId) => {
    // Always send the real user question. DocumentID already scopes the report.
    // Keep report name only as optional context prefix for multi-doc clarity.
    const contextualQuestion = reportName
      ? `Using report "${reportName}", answer this question: ${question}`
      : question;

    const resp = await dispatch(
      documentAssistantAsk({
        token,
        UserID: userId,
        DocumentID: documentId || 0,
        Question: contextualQuestion,
        sessionId: sessionId || undefined,
        signal,
      })
    ).unwrap();

    return parseAskPayload(resp);
  };

  const isNotIndexedAnswer = (answer = '') =>
    /no indexed documents were found|please send your report to continue/i.test(String(answer));

  const isAbortError = (err) =>
    err?.name === 'CanceledError' ||
    err?.name === 'AbortError' ||
    err?.code === 'ERR_CANCELED' ||
    err?.message === 'ASK_ACME_ABORTED';

  const handleSend = async (rawText) => {
    const question = (rawText ?? inputValue).trim();
    if (!question || isSending || !userId) return;

    let reportsAtSend = selectedReports.map((r) => ({ ...r }));
    let askDocsAtSend = selectedAskDocuments.map((d) => ({ ...d }));

    // Same chat follow-up: reuse documents from earlier in this session
    if (reportsAtSend.length === 0 && askDocsAtSend.length === 0 && Array.isArray(sessionDocs) && sessionDocs.length > 0) {
      askDocsAtSend = sessionDocs.map((d) => ({
        documentId: d.documentId,
        documentName: d.documentName,
      }));
    }

    // First question in a chat still requires select/upload
    if (reportsAtSend.length === 0 && askDocsAtSend.length === 0) {
      setErrorBanner('Please select a report from categories or upload new report.');
      return;
    }

    const attachedNames = [
      ...askDocsAtSend.map((d) => d.documentName),
      ...reportsAtSend.map((r) => r.reportName),
    ];

    const generation = ++sendGenerationRef.current;
    const sendSessionId = activeSessionId;
    const abortController = new AbortController();
    sendAbortRef.current = abortController;
    const { signal } = abortController;

    const isViewingSendSession = () => {
      if (sendSessionId == null) return activeSessionIdRef.current == null;
      return String(activeSessionIdRef.current) === String(sendSessionId);
    };

    const assertNotAborted = () => {
      if (generation !== sendGenerationRef.current || signal.aborted) {
        const abortErr = new Error('ASK_ACME_ABORTED');
        abortErr.name = 'AbortError';
        throw abortErr;
      }
    };

    setIsSending(true);
    setErrorBanner('');
    setInputValue('');

    const optimisticId = `tmp-${Date.now()}`;
    if (isViewingSendSession()) {
      setMessages((prev) => [
        ...prev,
        {
          messageId: optimisticId,
          role: 'user',
          message: question,
          createdOn: nowIso(),
          attachedReports: attachedNames,
        },
      ]);
    }

    try {
      let sessionId = sendSessionId;
      let answer = '';
      let chunks = [];
      const results = [];

      // Already-indexed uploads / session docs
      for (const doc of askDocsAtSend) {
        assertNotAborted();
        let result = await askWithDocument(
          doc.documentId,
          question,
          doc.documentName,
          signal,
          sessionId
        );
        assertNotAborted();
        if (result.sessionId != null) sessionId = result.sessionId;
        results.push({
          name: doc.documentName,
          documentId: Number(doc.documentId),
          ...result,
        });
      }

      // Your Reports — index then ask
      for (const report of reportsAtSend) {
        assertNotAborted();
        let documentId = await ensureIndexedDocumentId(report, { signal });
        assertNotAborted();
        let result = await askWithDocument(
          documentId,
          question,
          report.reportName,
          signal,
          sessionId
        );
        assertNotAborted();
        if (result.sessionId != null) sessionId = result.sessionId;
        if (isNotIndexedAnswer(result.answer)) {
          documentId = await ensureIndexedDocumentId(report, { forceReindex: true, signal });
          assertNotAborted();
          result = await askWithDocument(
            documentId,
            question,
            report.reportName,
            signal,
            sessionId
          );
          assertNotAborted();
          if (result.sessionId != null) sessionId = result.sessionId;
        }
        results.push({
          name: report.reportName,
          documentId: Number(documentId),
          ...result,
        });
      }

      sessionId = sessionId ?? results.find((r) => r.sessionId)?.sessionId ?? null;
      chunks = results.flatMap((r) => (Array.isArray(r.chunks) ? r.chunks : []));

      if (results.length === 1) {
        answer = results[0].answer;
      } else {
        answer = results
          .map((r) => {
            const body = (r.answer || '').trim() || 'No answer returned for this document.';
            return `📄 ${r.name}\n${body}`;
          })
          .join('\n\n————————————\n\n');
      }

      if (isNotIndexedAnswer(answer)) {
        throw new Error(
          'Document could not be indexed for Ask ACME. Please upload a PDF and try again.'
        );
      }

      assertNotAborted();

      const nextSessionDocs = results
        .filter((r) => r.documentId != null && !Number.isNaN(Number(r.documentId)))
        .map((r) => ({
          documentId: Number(r.documentId),
          documentName: r.name,
        }));
      const stillOnThisChat =
        sessionId == null ||
        String(activeSessionIdRef.current) === String(sendSessionId) ||
        String(activeSessionIdRef.current) === String(sessionId);

      // The ref normally updates in an effect after paint. On the first question
      // the API session id is new, so the reply would be stored but not shown
      // until a refresh. Point the ref at this chat before drawing the answer.
      if (stillOnThisChat) {
        activeSessionIdRef.current = sessionId ?? null;
        setActiveSessionId(sessionId ?? null);
        if (nextSessionDocs.length) setSessionDocs(nextSessionDocs);
        setSelectedReportIds([]);
        setSelectedAskDocIds([]);
      }

      appendAskExchange(
        sessionId,
        question,
        answer || 'No answer returned.',
        chunks,
        attachedNames
      );
      if (sessionId != null && nextSessionDocs.length) {
        updateHistoryMeta(sessionId, { sessionDocs: nextSessionDocs });
      }
      await loadHistory();
    } catch (err) {
      if (isAbortError(err) || generation !== sendGenerationRef.current) {
        // Keep the user message; stop only cancels the in-flight answer.
        return;
      }
      console.error('Ask ACME send failed', err);
      if (isViewingSendSession()) {
        setMessages((prev) => prev.filter((m) => m.messageId !== optimisticId));
        setErrorBanner(
          (typeof err === 'string' && err) ||
          err?.response?.data?.Message ||
          err?.response?.data?.message ||
          err?.message ||
          'Failed to get an answer. Please try again.'
        );
      }
    } finally {
      if (generation === sendGenerationRef.current) {
        setIsSending(false);
        sendAbortRef.current = null;
        inputRef.current?.focus();
      }
    }
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const formatTime = (value) => {
    if (!value) return '';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const displayName = useMemo(() => {
    const profileRow = usersDetailsGet?.Table?.[0] || usersDetailsGet?.table?.[0] || {};
    const loginRow = login?.Table?.[0] || login?.table?.[0] || {};
    const sources = [selectedFamilyMember, profileRow, login, loginRow];

    const pick = (...keys) => {
      for (const source of sources) {
        if (!source || typeof source !== 'object') continue;
        for (const key of keys) {
          const value = String(source[key] ?? '').trim();
          if (value && value !== 'undefined' && value !== 'null' && value !== '0') {
            return value;
          }
        }
      }
      return '';
    };

    const titleCase = (value) => {
      const text = String(value || '').trim();
      if (!text) return '';
      return text.charAt(0).toUpperCase() + text.slice(1);
    };

    const firstName = pick('first_name', 'First_Name', 'FirstName');
    const lastName = pick('last_name', 'Last_Name', 'LastName');
    const fullName = [titleCase(firstName), titleCase(lastName)].filter(Boolean).join(' ');
    if (fullName) return fullName;

    const raw =
      pick('relative_name') ||
      pick('user_name', 'UserName', 'User_Name', 'email_address', 'EmailAddress', 'email') ||
      pick('dropdown_display_name');
    if (!raw) return 'there';
    const withoutBracket = raw.replace(/\s*\[[^\]]*\]\s*$/, '').trim();
    const local = withoutBracket.includes('@') ? withoutBracket.split('@')[0] : withoutBracket;
    const name = local.replace(/[._]+/g, ' ').trim();
    if (!name) return 'there';
    return name
      .split(/\s+/)
      .map((part) => titleCase(part))
      .join(' ');
  }, [selectedFamilyMember, login, usersDetailsGet]);

  
  const startVoiceInput = () => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      setErrorBanner('Voice input is not supported in this browser.');
      return;
    }
    const recognition = new SpeechRecognition();
    recognition.lang = 'en-US';
    recognition.interimResults = false;
    recognition.onresult = (event) => {
      const transcript = event.results?.[0]?.[0]?.transcript;
      if (transcript) setInputValue((prev) => (prev ? `${prev} ${transcript}` : transcript));
    };
    recognition.onerror = () => setErrorBanner('Could not capture voice input.');
    recognition.start();
  };

  const showEmptyState = messages.length === 0 && !isSending && activeSessionId == null;
  const hasDocumentsSelected =
    selectedReports.length > 0 ||
    selectedAskDocuments.length > 0 ||
    (Array.isArray(sessionDocs) && sessionDocs.length > 0);
  const canAsk = hasDocumentsSelected && !isSending;

  const composer = (
    <div className={`ask-acme-composer${showEmptyState ? ' ask-acme-composer--hero' : ''}`}>
      {!showEmptyState && <AskAcmeDisclaimer />}
      <div className="ask-acme-pill">
        <button
          type="button"
          className={`ask-acme-pill-icon-btn ask-acme-tooltip${isUploading ? ' is-uploading' : ''}`}
          onClick={handleUploadClick}
          disabled={isUploading || isSending}
          data-tooltip={isUploading ? 'Uploading…' : 'Upload report'}
          aria-label={isUploading ? 'Uploading report' : 'Upload report'}
        >
          {isUploading ? <span className="ask-acme-btn-spinner" aria-hidden /> : '+'}
        </button>
        <input
          ref={inputRef}
          className="ask-acme-pill-input"
          placeholder={
            isSending
              ? 'Searching…'
              : hasDocumentsSelected
                ? 'Ask anything'
                : 'Select or upload a report to ask'
          }
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={onKeyDown}
          disabled={isSending}
        />
        <button
          type="button"
          className="ask-acme-pill-icon-btn ask-acme-pill-mic ask-acme-tooltip"
          onClick={startVoiceInput}
          disabled={isSending || !hasDocumentsSelected}
          data-tooltip="Voice input"
          aria-label="Voice input"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
            <path d="M12 14a3 3 0 0 0 3-3V7a3 3 0 1 0-6 0v4a3 3 0 0 0 3 3Z" stroke="currentColor" strokeWidth="1.8" />
            <path d="M19 11a7 7 0 0 1-14 0M12 18v3" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
          </svg>
        </button>
        {isSending ? (
          <button
            type="button"
            className="ask-acme-pill-stop ask-acme-tooltip"
            onClick={handleStopSend}
            data-tooltip="Stop"
            aria-label="Stop searching"
          >
            <span className="ask-acme-pill-stop-square" aria-hidden />
          </button>
        ) : (
          <button
            type="button"
            className="ask-acme-pill-send"
            onClick={() => handleSend()}
            disabled={!canAsk || !inputValue.trim()}
            aria-label="Send"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
              <path d="M5 12h12M13 6l6 6-6 6" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        )}
      </div>
    </div>
  );

  return (
    <div className={`ask-acme-page${showEmptyState ? ' is-empty' : ''}`}>
      <aside className="ask-acme-sidebar">
        {/* <div className="ask-acme-sidebar-brand">
          <div className="ask-acme-sidebar-brand-mark">AI</div>
          <div>
            <div className="ask-acme-sidebar-brand-title">Ask ACME</div>
            <div className="ask-acme-sidebar-brand-sub">Report-aware health assistant</div>
          </div>
        </div> */}

        <button type="button" className="ask-acme-new-chat-btn" onClick={handleNewChat}>
          <svg className="ask-acme-new-chat-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
            <path
              d="M12 20h9"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
            />
            <path
              d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5Z"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinejoin="round"
            />
          </svg>
          New chat
        </button>

        <div className="ask-acme-sidebar-section ask-acme-reports-section">
          <div className="ask-acme-report-list">
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.doc,.docx,.txt,image/*"
              className="ask-acme-file-input"
              onChange={handleFileChange}
            />

            {isLoadingReports && (
              <div className="ask-acme-skeleton-list">
                <div className="ask-acme-skeleton" />
                <div className="ask-acme-skeleton" />
                <div className="ask-acme-skeleton" />
              </div>
            )}

            {!isLoadingReports && reports.length === 0 && askDocuments.length === 0 && (
              <div className="ask-acme-empty-reports">
                <div className="ask-acme-empty-reports-title">No reports found</div>
                {/* <div className="ask-acme-muted">Use + to add a PDF for Ask ACME.</div> */}
              </div>
            )}

            {!isLoadingReports && visibleItemCount > 0 && (
              <>
                {/* All — same pattern as Share Everywhere */}
                <button
                  type="button"
                  className={`ask-acme-tree-row ask-acme-tree-all${allVisibleSelected ? ' is-checked' : ''}${someVisibleSelected ? ' is-partial' : ''}`}
                  onClick={handleToggleSelectAllVisible}
                  aria-pressed={allVisibleSelected}
                >
                  <span
                    className={`ask-acme-check${allVisibleSelected ? ' is-checked' : ''}${someVisibleSelected ? ' is-partial' : ''}`}
                    aria-hidden
                  >
                    <span className="ask-acme-check-box">
                      {allVisibleSelected ? '✓' : someVisibleSelected ? '–' : ''}
                    </span>
                  </span>
                  <span className="ask-acme-tree-label">All</span>
                </button>

                {askDocuments.length > 0 && (
                  <div className="ask-acme-tree-group">
                    <div
                      className={`ask-acme-tree-row ask-acme-tree-category${uploadsSelection.all ? ' is-checked' : ''}${uploadsSelection.some ? ' is-partial' : ''}`}
                    >
                      <button
                        type="button"
                        className="ask-acme-tree-check-btn"
                        onClick={handleToggleUploadsCategory}
                        aria-label={`${uploadsSelection.all ? 'Deselect' : 'Select'} Uploads`}
                      >
                        <span
                          className={`ask-acme-check${uploadsSelection.all ? ' is-checked' : ''}${uploadsSelection.some ? ' is-partial' : ''}`}
                          aria-hidden
                        >
                          <span className="ask-acme-check-box">
                            {uploadsSelection.all ? '✓' : uploadsSelection.some ? '–' : ''}
                          </span>
                        </span>
                      </button>
                      <button
                        type="button"
                        className={`ask-acme-tree-expand${uploadsOpen ? ' is-open' : ''}`}
                        onClick={() => toggleCategoryExpand('__uploads__')}
                        aria-expanded={uploadsOpen}
                        aria-label={`${uploadsOpen ? 'Collapse' : 'Expand'} Uploads`}
                      >
                        <span className="ask-acme-tree-chevron" aria-hidden>›</span>
                      </button>
                      <button
                        type="button"
                        className="ask-acme-tree-name-btn"
                        onClick={handleToggleUploadsCategory}
                      >
                        <span className="ask-acme-tree-name-stack">
                          <span className="ask-acme-tree-label">Uploads</span>
                          {askDocuments.some((d) => !d.filedGroupName) ? (
                            <span className="ask-acme-upload-group-hint">
                              Goes to Your Reports in ~5 min
                            </span>
                          ) : null}
                        </span>
                      </button>
                    </div>
                    {uploadsOpen &&
                      askDocuments.map((doc) => {
                        const checked = selectedAskDocIds.includes(doc.documentId);
                        const filingLabel = getUploadFilingLabel(doc, nowTs);
                        return (
                          <div
                            key={`ask-doc-${doc.documentId}`}
                            className={`ask-acme-tree-row ask-acme-tree-leaf ask-acme-upload-leaf${checked ? ' is-checked' : ''}`}
                          >
                            <label className="ask-acme-upload-leaf-check">
                              <span className={`ask-acme-check${checked ? ' is-checked' : ''}`}>
                                <input
                                  type="checkbox"
                                  checked={checked}
                                  onChange={() => handleToggleAskDocument(doc.documentId)}
                                />
                                <span className="ask-acme-check-box" aria-hidden>
                                  {checked ? '✓' : ''}
                                </span>
                              </span>
                              <span className="ask-acme-tree-leaf-meta">
                                <span
                                  className="ask-acme-tree-label ask-acme-tooltip"
                                  data-tooltip={doc.documentName}
                                >
                                  {doc.documentName}
                                </span>
                                <span
                                  className={`ask-acme-upload-filing${doc.filedGroupName ? ' is-filed' : ''}`}
                                >
                                  {filingLabel}
                                </span>
                              </span>
                            </label>
                          </div>
                        );
                      })}
                  </div>
                )}

                {reportCategories.map((category) => {
                  const items = Array.isArray(category.items) ? category.items : [];
                  const categoryIds = items.map((r) => r.reportId);
                  const open = isCategoryOpen(category.name);
                  const { all: catAll, some: catSome } = getCategorySelectionState(
                    categoryIds,
                    selectedReportIds
                  );
                  return (
                    <div className="ask-acme-tree-group" key={category.name}>
                      <div
                        className={`ask-acme-tree-row ask-acme-tree-category${catAll ? ' is-checked' : ''}${catSome ? ' is-partial' : ''}`}
                      >
                        <button
                          type="button"
                          className="ask-acme-tree-check-btn"
                          onClick={() => handleToggleCategoryReports(categoryIds)}
                          aria-label={`${catAll ? 'Deselect' : 'Select'} ${category.name}`}
                        >
                          <span
                            className={`ask-acme-check${catAll ? ' is-checked' : ''}${catSome ? ' is-partial' : ''}`}
                            aria-hidden
                          >
                            <span className="ask-acme-check-box">
                              {catAll ? '✓' : catSome ? '–' : ''}
                            </span>
                          </span>
                        </button>
                        <button
                          type="button"
                          className={`ask-acme-tree-expand${open ? ' is-open' : ''}`}
                          onClick={() => toggleCategoryExpand(category.name)}
                          aria-expanded={open}
                          aria-label={`${open ? 'Collapse' : 'Expand'} ${category.name}`}
                        >
                          <span className="ask-acme-tree-chevron" aria-hidden>›</span>
                        </button>
                        <button
                          type="button"
                          className="ask-acme-tree-name-btn"
                          onClick={() => handleToggleCategoryReports(categoryIds)}
                        >
                          <span className="ask-acme-tree-label">{category.name}</span>
                        </button>
                      </div>
                      {open &&
                        items.map((report) => {
                          const checked = selectedReportIds.includes(report.reportId);
                          const dateLabel = formatShortDate(report.reportDate);
                          const recent = recentUploads[report.reportId];
                          const filingLabel = recent?.filedGroup
                            ? `Now in ${recent.filedGroup}`
                            : recent?.uploadedAt
                              ? getUploadFilingLabel({ uploadedAt: recent.uploadedAt }, nowTs)
                              : '';
                          const leafTip = [report.reportName, filingLabel || dateLabel]
                            .filter(Boolean)
                            .join(' · ');
                          return (
                            <label
                              key={report.reportId}
                              className={`ask-acme-tree-row ask-acme-tree-leaf${checked ? ' is-checked' : ''}`}
                            >
                              <span className={`ask-acme-check${checked ? ' is-checked' : ''}`}>
                                <input
                                  type="checkbox"
                                  checked={checked}
                                  onChange={() => handleToggleReport(report.reportId)}
                                />
                                <span className="ask-acme-check-box" aria-hidden>
                                  {checked ? '✓' : ''}
                                </span>
                              </span>
                              <span className="ask-acme-tree-leaf-meta">
                                <span className="ask-acme-tree-label ask-acme-tooltip" data-tooltip={leafTip}>
                                  {report.reportName}
                                </span>
                                {filingLabel ? (
                                  <span
                                    className={`ask-acme-upload-filing${recent?.filedGroup ? ' is-filed' : ''}`}
                                  >
                                    {filingLabel}
                                  </span>
                                ) : dateLabel ? (
                                  <span className="ask-acme-tree-leaf-date">{dateLabel}</span>
                                ) : null}
                              </span>
                            </label>
                          );
                        })}
                    </div>
                  );
                })}
              </>
            )}
          </div>
        </div>

        <div className={`ask-acme-sidebar-section ask-acme-history-section${historyExpanded ? ' is-expanded' : ' is-collapsed'}`}>
          <button
            type="button"
            className="ask-acme-history-head"
            onClick={() => {
              if (!historyHasMore && !historyExpanded) return;
              setHistoryExpanded((prev) => !prev);
            }}
            aria-expanded={historyExpanded}
            aria-label={historyExpanded ? 'Collapse chat history' : 'Expand chat history'}
          >
            <span className="ask-acme-section-title">Chat History</span>
            {visibleSessions.length > 0 ? (
              <span
                className={`ask-acme-history-toggle${historyExpanded ? ' is-open' : ''}${!historyHasMore ? ' is-disabled' : ''}`}
                aria-hidden
              >
                ›
              </span>
            ) : null}
          </button>
          <div className="ask-acme-history-list" style={{ '--ask-history-rows': HISTORY_VISIBLE_ROWS }}>
            {isLoadingHistory && <div className="ask-acme-muted">Loading...</div>}
            {!isLoadingHistory && visibleSessions.length === 0 && (
              <div className="ask-acme-muted">Your conversations will appear here.</div>
            )}
            {visibleSessions.map((session) => {
              const isActive = String(activeSessionId) === String(session.sessionId);
              const isMenuOpen = String(historyMenuId) === String(session.sessionId);
              const isRenaming = String(renamingSessionId) === String(session.sessionId);
              const isDeleting = String(deletingSessionId) === String(session.sessionId);

              return (
                <div
                  key={session.sessionId}
                  className={`ask-acme-history-item${isActive ? ' active' : ''}${isMenuOpen ? ' menu-open' : ''}`}
                >
                  {isRenaming ? (
                    <input
                      ref={renameInputRef}
                      className="ask-acme-history-rename-input"
                      value={renameValue}
                      onChange={(e) => setRenameValue(e.target.value)}
                      onBlur={() => handleCommitRenameHistory(session)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleCommitRenameHistory(session);
                        }
                        if (e.key === 'Escape') {
                          e.preventDefault();
                          setRenamingSessionId(null);
                        }
                      }}
                    />
                  ) : (
                    <button
                      type="button"
                      className="ask-acme-history-main"
                      onClick={() => handleSelectHistory(session)}
                    >
                      <span className="ask-acme-history-title">
                        <span className="ask-acme-history-title-text">{session.displayTitle}</span>
                      </span>
                    </button>
                  )}

                  <div className={`ask-acme-history-actions${isMenuOpen ? ' is-open' : ''}`}>
                    <button
                      type="button"
                      className="ask-acme-history-more ask-acme-tooltip"
                      data-tooltip="More options"
                      aria-label="More options"
                      aria-expanded={isMenuOpen}
                      disabled={deletingSessionId != null}
                      onClick={(e) => handleOpenHistoryMenu(e, session)}
                    >
                      {isDeleting ? '…' : '⋯'}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </aside>

      <main className="ask-acme-main">
        {showEmptyState ? (
          <div className="ask-acme-hero">
            <h1 className="ask-acme-hero-greeting">
              Hey! {displayName}. Ready to get started?
            </h1>

            {composer}

            <div className="ask-acme-suggest">
              <p className="ask-acme-suggest-heading">Try asking</p>
              <div className="ask-acme-suggest-grid">
                {SUGGESTED_QUESTIONS.map((item) => (
                  <button
                    type="button"
                    key={item.id}
                    className="ask-acme-suggest-card"
                    disabled={isSending}
                    onClick={() => handleSend(item.question)}
                  >
                    <span className="ask-acme-suggest-icon" aria-hidden>
                      <SuggestedQuestionIcon type={item.icon} />
                    </span>
                    <span className="ask-acme-suggest-copy">
                      <span className="ask-acme-suggest-label">{item.label}</span>
                      <span className="ask-acme-suggest-text">{item.question}</span>
                    </span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        ) : (
          <>
            <div className="ask-acme-chat-body" ref={chatBodyRef}>
              {(Array.isArray(messages) ? messages : []).map((msg, msgIndex) => {
                const chunks = Array.isArray(msg?.retrievedChunks) ? msg.retrievedChunks : [];
                const visibleAttachedReports = getVisibleAttachedReports(messages, msgIndex);
                return (
                  <div
                    key={msg.messageId}
                    className={`ask-acme-bubble-row ${msg.role === 'assistant' ? 'assistant' : 'user'}`}
                  >
                    <div className={`ask-acme-bubble ${msg.role === 'assistant' ? 'assistant' : 'user'}`}>
                      {visibleAttachedReports.length > 0 && (
                        <div className="ask-acme-bubble-context">
                          Using: {visibleAttachedReports.join(', ')}
                        </div>
                      )}
                      <div className="ask-acme-bubble-text">{msg.message}</div>
                      {chunks.length > 0 && (
                        <details className="ask-acme-chunks">
                          <summary>Sources ({chunks.length})</summary>
                          <ul>
                            {chunks.map((chunk, idx) => (
                              <li key={chunk?.ChunkID ?? chunk?.chunkId ?? idx}>
                                <strong>
                                  {chunk?.DocumentName || chunk?.documentName || 'Source'}
                                  {chunk?.ChunkIndex != null || chunk?.chunkIndex != null
                                    ? ` #${chunk?.ChunkIndex ?? chunk?.chunkIndex}`
                                    : ''}
                                </strong>
                                <div>{chunk?.ChunkText || chunk?.chunkText || ''}</div>
                              </li>
                            ))}
                          </ul>
                        </details>
                      )}
                      {msg.createdOn ? (
                        <div className="ask-acme-bubble-time">{formatTime(msg.createdOn)}</div>
                      ) : null}
                    </div>
                  </div>
                );
              })}

              {isSending && (
                <div className="ask-acme-bubble-row assistant">
                  <div className="ask-acme-bubble assistant ask-acme-typing">
                    <span /><span /><span />
                  </div>
                </div>
              )}
            </div>

            {composer}
          </>
        )}
      </main>

      {historyMenuId != null && historyMenuPos ? (
        <div
          ref={historyMenuRef}
          className="ask-acme-history-menu ask-acme-history-menu--fixed"
          role="menu"
          style={{ top: historyMenuPos.top, left: historyMenuPos.left }}
        >
          {(() => {
            const menuSession =
              visibleSessions.find((s) => String(s.sessionId) === String(historyMenuId)) || null;
            if (!menuSession) return null;
            return (
              <>
                <button
                  type="button"
                  role="menuitem"
                  onClick={handleShareHistory}
                >
                  <HistoryMenuIcon type="share" />
                  <span>Share</span>
                </button>
                <button
                  type="button"
                  role="menuitem"
                  onClick={(e) => handleStartRenameHistory(e, menuSession)}
                >
                  <HistoryMenuIcon type="rename" />
                  <span>Rename</span>
                </button>
                <button
                  type="button"
                  role="menuitem"
                  className="is-danger"
                  disabled={deletingSessionId != null}
                  onClick={(e) => handleRequestDeleteHistory(e, menuSession)}
                >
                  <HistoryMenuIcon type="delete" />
                  <span>Delete</span>
                </button>
              </>
            );
          })()}
        </div>
      ) : null}

      {deleteConfirmSession ? (
        <div className="ask-acme-delete-overlay" role="presentation" onClick={handleCancelDeleteHistory}>
          <div
            className="ask-acme-delete-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="ask-acme-delete-title"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 id="ask-acme-delete-title" className="ask-acme-delete-title">
              Delete chat?
            </h3>
            <p className="ask-acme-delete-text">
              This will delete{' '}
              <strong>
                {deleteConfirmSession.displayTitle ||
                  deleteConfirmSession.sessionTitle ||
                  'this chat'}
              </strong>
              .
            </p>
            <div className="ask-acme-delete-actions">
              <button
                type="button"
                className="ask-acme-delete-cancel"
                onClick={handleCancelDeleteHistory}
                disabled={deletingSessionId != null}
              >
                Cancel
              </button>
              <button
                type="button"
                className="ask-acme-delete-confirm"
                onClick={handleConfirmDeleteHistory}
                disabled={deletingSessionId != null}
              >
                {deletingSessionId != null ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {isUploading ? (
        <div className="ask-acme-upload-overlay" role="alertdialog" aria-busy="true" aria-label="Uploading report">
          <div className="ask-acme-upload-card">
            <span className="ask-acme-upload-spinner ask-acme-upload-spinner--lg" aria-hidden />
            <h3 className="ask-acme-upload-title">Uploading report…</h3>
            <p className="ask-acme-upload-filename">{uploadingFileName || 'Please wait'}</p>
            <p className="ask-acme-upload-hint">This may take a few seconds. You can keep chatting after it finishes.</p>
          </div>
        </div>
      ) : null}

      {filingNotice ? (
        <div className="ask-acme-filing-toast" role="status" aria-live="polite">
          <div className="ask-acme-filing-toast-copy">
            <strong>{filingNotice.title}</strong>
            <span>{filingNotice.body}</span>
          </div>
          <button
            type="button"
            className="ask-acme-filing-toast-close"
            onClick={() => setFilingNotice(null)}
            aria-label="Dismiss"
          >
            ×
          </button>
        </div>
      ) : null}

      <CustomAlertModal
        show={Boolean(errorBanner)}
        onHide={clearErrorBanner}
        message={errorBanner}
      />

      <CustomSuccessModal
        show={showDeleteSuccessModal}
        onHide={() => setShowDeleteSuccessModal(false)}
        message={deleteSuccessMessage}
        name=""
      />

      {showComingSoonModal ? (
        <div className="custom-modal-overlay ask-acme-coming-soon-overlay" role="presentation">
          <div
            className="custom-modal ask-acme-coming-soon-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="ask-acme-coming-soon-title"
          >
            <div className="ask-acme-coming-soon-icon-wrap">
              <div className="ask-acme-coming-soon-icon">
                <span className="ask-acme-coming-soon-bang">!</span>
              </div>
            </div>
            <h4 id="ask-acme-coming-soon-title" className="ask-acme-coming-soon-title">
              Coming Soon
            </h4>
            <div className="ask-acme-coming-soon-actions">
              <button
                type="button"
                className="btn ask-acme-coming-soon-btn"
                onClick={() => setShowComingSoonModal(false)}
              >
                OK
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default AskAcme;
